import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { appThemeColors } from "styles/appTheme";
import { appThemeFonts } from "styles/appThemeFonts";
import { appThemeRadius, appThemeShadow } from "styles/appThemeTokens";

const uiFont = appThemeFonts.family.ui;

const appDialogButtonText = {
  fontFamily: uiFont,
  fontSize: appThemeFonts.size.base,
  fontWeight: appThemeFonts.weight.semibold,
};

export const appDialogPaperSx = {
  width: "min(430px, calc(100vw - 32px))",
  margin: 0,
  borderRadius: appThemeRadius.lg,
  backgroundColor: appThemeColors.background.paper,
  boxShadow: appThemeShadow.menu,
};

export const appDialogBackdropSx = {
  backgroundColor: "rgba(10,31,58,.42)",
};

export const AppDialogHeader = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 12,
  padding: "14px 20px 12px",
  borderBottom: `1px solid ${appThemeColors.border.default}`,
});

export const AppDialogHeading = styled(Typography)({
  fontFamily: uiFont,
  fontSize: appThemeFonts.size.xl,
  fontWeight: appThemeFonts.weight.bold,
  color: appThemeColors.text.heading,
  margin: 0,
  lineHeight: 1.3,
  flex: 1,
  minWidth: 0,
});

export const AppDialogCloseButton = styled(IconButton)({
  width: 30,
  height: 30,
  flex: "0 0 auto",
  marginTop: -4,
  marginRight: -6,
  borderRadius: appThemeRadius.md,
  color: appThemeColors.text.muted,
  "&:hover": {
    backgroundColor: appThemeColors.background.kbd,
  },
  "&:focus-visible": {
    outline: "2px solid #0B5FA5",
    outlineOffset: "2px",
  },
});

export const AppDialogCloseIcon = styled(CloseIcon)({
  width: 15,
  height: 15,
  fontSize: 15,
});

export const AppDialogSubtitle = styled(Typography)({
  fontFamily: uiFont,
  fontSize: appThemeFonts.size.mdPlus,
  fontWeight: appThemeFonts.weight.regular,
  color: appThemeColors.text.muted,
  margin: 0,
  padding: "20px 20px 0",
  lineHeight: 1.4,
});

export const AppDialogBody = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "$hasSubtitle",
})(({ $hasSubtitle }) => ({
  fontFamily: uiFont,
  fontSize: appThemeFonts.size.base,
  color: appThemeColors.text.primary,
  padding: $hasSubtitle ? "0 20px 20px" : 20,
  lineHeight: 1.55,
  whiteSpace: "pre-line",
}));

export const AppDialogFooter = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  gap: 10,
  padding: "12px 20px",
  borderTop: `1px solid ${appThemeColors.border.default}`,
});

export const AppDialogCancelButton = styled(Button)({
  ...appDialogButtonText,
  color: appThemeColors.text.lightBlue,
  textTransform: "none",
  borderRadius: appThemeRadius.pill,
  border: `1px solid ${appThemeColors.border.strong}`,
  backgroundColor: "transparent",
  boxShadow: "none",
  "&:hover": {
    boxShadow: "none",
    backgroundColor: appThemeColors.background.kbd,
    borderColor: appThemeColors.text.lightBlue,
    color: appThemeColors.text.primary,
  },
  "&.Mui-disabled": {
    opacity: 0.5,
  },
  "&.Mui-disabled:hover": {
    backgroundColor: "transparent",
    borderColor: appThemeColors.border.strong,
    color: appThemeColors.text.lightBlue,
  },
  "&:focus-visible": {
    outline: "2px solid #0B5FA5",
    outlineOffset: "2px",
  },
});

export const AppDialogConfirmButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "colorVariant",
})(({ colorVariant = "primary" }) => {
  const backgroundColor =
    colorVariant === "danger"
      ? appThemeColors.semantic.danger
      : appThemeColors.text.lightBlue;

  return {
    ...appDialogButtonText,
    color: appThemeColors.neutral.white,
    textTransform: "none",
    borderRadius: "18px",
    backgroundColor,
    boxShadow: "none",
    "&:hover": {
      boxShadow: "none",
      backgroundColor,
    },
    "&.Mui-disabled, &.MuiButton-contained.Mui-disabled": {
      opacity: 0.5,
      pointerEvents: "auto",
      cursor: "not-allowed",
      color: appThemeColors.neutral.white,
      backgroundColor,
    },
    "&:focus-visible": {
      outline: "2px solid #0B5FA5",
      outlineOffset: "2px",
    },
  };
});
